import React from 'react';
import { TONEFILL, useWidth } from '../../charts.js';
import { BODY } from '../../primitives.js';

const h = React.createElement;

/**
 * Rounded bars, bold ink edges, gold peak, dotted goal.
 *
 * @param {object} p
 * @param {{ label: string; value: number; tone?: 'lime'|'gold'|'coral'|'blue'|'purple'|'ink' }[]} p.data
 * @param {number} [p.height]
 * @param {number} [p.goal]
 * @param {(v: number) => string} [p.format]
 * @param {string} [p.label]
 * @param {'max'|'none'} [p.highlight]
 * @param {'lime'|'gold'|'coral'|'blue'|'purple'|'ink'} [p.tone]
 * @param {boolean} [p.showValues]
 */
export function BarChart({ data = [], height = 160, goal, format = (v) => String(v), label, highlight = 'max', tone = 'lime', showValues }) {
  const ref = React.useRef(null), W = useWidth(ref, 480);
  const [hover, setHover] = React.useState(-1);
  const max = Math.max(goal || 0, ...data.map((d) => d.value)) * 1.08 || 1;
  const top = 14, bottom = 22, ch = height - top - bottom, n = data.length || 1;
  const slot = W / n, bw = Math.max(6, Math.min(44, slot * .62));
  const maxI = data.reduce((b, d, i) => (d.value > (data[b] || { value: -Infinity }).value ? i : b), 0);
  const gy = goal != null ? top + ch * (1 - goal / max) : null;
  const goalTag = (y) => h('g', null, h('line', { x1: 4, x2: W - 4, y1: y, y2: y, stroke: 'var(--ink)', strokeWidth: 2.5, strokeDasharray: '1 7', strokeLinecap: 'round' }), h('rect', { x: 0, y: y - 10, width: 58 + String(format(goal)).length * 6, height: 20, rx: 10, fill: 'var(--card)', stroke: 'var(--ink)', strokeWidth: 2 }), h('text', { x: 10, y: y + 4, style: { fontSize: 10.5, fontWeight: 800, fill: 'var(--tx)' } }, 'Goal ' + format(goal)));
  return h('div', { ref, style: { position: 'relative', width: '100%', minWidth: 0, height, fontFamily: BODY } }, W > 0 &&
    h('svg', { width: W, height, role: 'img', 'aria-label': label || 'Bar chart', style: { display: 'block', overflow: 'visible' } },
      h('line', { x1: 4, x2: W - 4, y1: top + ch + 1, y2: top + ch + 1, stroke: 'var(--ink)', strokeWidth: 3, strokeLinecap: 'round' }),
      data.map((d, i) => { const bh = Math.max(Math.min(bw, 20), ch * (d.value / max)); const x = i * slot + (slot - bw) / 2, y = top + ch - bh; const fill = TONEFILL[d.tone] || (highlight === 'max' && i === maxI ? 'var(--gold)' : TONEFILL[tone]);
        return h('g', { key: i, onPointerEnter: () => setHover(i), onPointerLeave: () => setHover(-1) },
          h('rect', { x: i * slot, y: top, width: slot, height: ch, fill: 'transparent' }),
          h('rect', { className: 'gx-bar', style: { animationDelay: i * 35 + 'ms', transformOrigin: `${x + bw / 2}px ${top + ch}px` }, x, y, width: bw, height: bh, rx: Math.min(bw / 2, 10), fill, stroke: 'var(--ink)', strokeWidth: 2.5 }),
          showValues || hover === i ? h('text', { x: x + bw / 2, y: y - 6, textAnchor: 'middle', style: { fontSize: 11, fontWeight: 800, fill: 'var(--tx)', fontVariantNumeric: 'tabular-nums' } }, format(d.value)) : null,
          h('text', { x: x + bw / 2, y: height - 4, textAnchor: 'middle', style: { fontSize: 10.5, fontWeight: 600, fill: hover === i ? 'var(--tx)' : 'var(--mut2)' } }, d.label)); }),
      gy != null ? goalTag(gy) : null));
}
